import { sessionStore } from 'utils/storage'

const DESKTOP_ANALYTICS_CONTEXT_KEY = 'cf-studio-desktop-context'

/**
 * Desktop shell context attached to every Amplitude event fired from the Studio
 * desktop renderer, so downstream metrics (trial starts, opens, …) can be split
 * by OS/app version without each caller passing it. Absent on the web.
 */
export interface DesktopAnalyticsContext {
  studio_runtime: 'desktop'
  desktop_platform: 'mac' | 'windows' | 'linux' | 'unknown'
  desktop_app_version: string | null
}

/** Storage surface the context store needs; satisfied by `sessionStore`. */
type DesktopContextStore = Pick<typeof sessionStore, 'getJSON' | 'setJSON'>

/**
 * Builds a desktop-context store over an injectable persistence layer. The
 * store keeps an in-memory cache to avoid a storage read on every event, and
 * falls back to the injected store so context registered before a reload (or
 * before a queued-event drain) still applies within the session.
 *
 * The `store` seam exists for tests: pass a fake to verify persistence and the
 * reload fallback without touching real `sessionStorage` or resetting modules.
 */
export function createDesktopAnalyticsContextStore(store: DesktopContextStore = sessionStore) {
  let cachedContext: DesktopAnalyticsContext | null = null

  return {
    set(context: DesktopAnalyticsContext) {
      cachedContext = context
      store.setJSON(DESKTOP_ANALYTICS_CONTEXT_KEY, context)
    },
    get(): DesktopAnalyticsContext | null {
      if (!cachedContext) {
        cachedContext = store.getJSON<DesktopAnalyticsContext>(DESKTOP_ANALYTICS_CONTEXT_KEY)
      }
      return cachedContext
    },
  }
}

const desktopAnalyticsContextStore = createDesktopAnalyticsContextStore()

/**
 * Registers the desktop shell context. Persisted via the store so events fired
 * before the registering hook re-runs (after a reload, or from the queued-event
 * drain) still carry it within the session.
 */
export const setDesktopAnalyticsContext = (context: DesktopAnalyticsContext) => {
  desktopAnalyticsContextStore.set(context)
}

export const getDesktopAnalyticsContext = (): DesktopAnalyticsContext | null => {
  return desktopAnalyticsContextStore.get()
}
